/**
 * Physics Engine
 * Matter.js wrapper for floating button physics
 */

import Matter from 'matter-js';
import { ButtonColor } from './SequenceManager';

export interface PhysicsBody {
  color: ButtonColor;
  body: Matter.Body;
}

export class PhysicsEngine {
  private engine: Matter.Engine;
  private runner: Matter.Runner | null = null;
  private bodies: Map<ButtonColor, Matter.Body> = new Map();
  private walls: Matter.Body[] = [];
  private buttonRadius: number = 40;

  constructor() {
    this.engine = Matter.Engine.create({
      gravity: { x: 0, y: 0, scale: 0 } // No gravity for floating effect
    });
  }

  /**
   * Initialize physics world with board dimensions
   */
  init(width: number, height: number, buttonRadius: number = 40): void {
    this.buttonRadius = buttonRadius;

    // Clear existing bodies
    Matter.Composite.clear(this.engine.world, false);
    this.bodies.clear();
    this.walls = [];

    // Create boundary walls (invisible)
    const wallThickness = 50;
    const wallOptions = {
      isStatic: true,
      restitution: 0.8, // Elastic bouncing
      friction: 0,
      frictionAir: 0
    };

    this.walls = [
      // Top wall
      Matter.Bodies.rectangle(width / 2, -wallThickness / 2, width, wallThickness, wallOptions),
      // Bottom wall
      Matter.Bodies.rectangle(width / 2, height + wallThickness / 2, width, wallThickness, wallOptions),
      // Left wall
      Matter.Bodies.rectangle(-wallThickness / 2, height / 2, wallThickness, height, wallOptions),
      // Right wall
      Matter.Bodies.rectangle(width + wallThickness / 2, height / 2, wallThickness, height, wallOptions)
    ];

    Matter.Composite.add(this.engine.world, this.walls);
  }

  /**
   * Create a physics body for a button
   */
  createButton(color: ButtonColor, x: number, y: number, vx: number = 0, vy: number = 0): Matter.Body {
    const body = Matter.Bodies.circle(x, y, this.buttonRadius, {
      restitution: 0.9, // Very elastic
      friction: 0,
      frictionAir: 0.002, // Minimal air resistance
      density: 0.001,
      label: color
    });

    // Set initial velocity
    Matter.Body.setVelocity(body, { x: vx, y: vy });

    this.bodies.set(color, body);
    Matter.Composite.add(this.engine.world, body);

    return body;
  }

  /**
   * Remove a button body
   */
  removeButton(color: ButtonColor): void {
    const body = this.bodies.get(color);
    if (body) {
      Matter.Composite.remove(this.engine.world, body);
      this.bodies.delete(color);
    }
  }

  /**
   * Get button body by color
   */
  getBody(color: ButtonColor): Matter.Body | undefined {
    return this.bodies.get(color);
  }

  /**
   * Get all button bodies
   */
  getAllBodies(): Map<ButtonColor, Matter.Body> {
    return this.bodies;
  }

  /**
   * Start physics simulation
   */
  start(): void {
    if (!this.runner) {
      this.runner = Matter.Runner.create({
        delta: 1000 / 60, // 60 FPS
        isFixed: true
      });
      Matter.Runner.run(this.runner, this.engine);
    }
  }

  /**
   * Stop physics simulation
   */
  stop(): void {
    if (this.runner) {
      Matter.Runner.stop(this.runner);
      this.runner = null;
    }
  }

  /**
   * Update physics (called each frame)
   */
  update(): void {
    // Matter.js handles updates via the runner
    // This method can be used for custom logic if needed
  }

  /**
   * Reset all velocities and positions
   */
  resetPositions(positions: Record<ButtonColor, { x: number; y: number }>): void {
    Object.entries(positions).forEach(([color, pos]) => {
      const body = this.bodies.get(color as ButtonColor);
      if (body) {
        Matter.Body.setPosition(body, { x: pos.x, y: pos.y });
        Matter.Body.setVelocity(body, { 
          x: (Math.random() - 0.5) * 2, 
          y: (Math.random() - 0.5) * 2 
        });
      }
    });
  }

  /**
   * Give random velocities to all buttons
   */
  randomizeVelocities(maxSpeed: number = 3): void {
    this.bodies.forEach(body => {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * maxSpeed;
      Matter.Body.setVelocity(body, {
        x: Math.cos(angle) * speed,
        y: Math.sin(angle) * speed
      });
    });
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.stop();
    Matter.Composite.clear(this.engine.world, false);
    Matter.Engine.clear(this.engine);
    this.bodies.clear();
  }
}
